import { createSelector } from '@reduxjs/toolkit'

import type { RootState } from '@/store/store'

import type { UserInfoResponse } from './Users.types'

////////////////////////////////////////////////////////////////////////
// Basic user selectors
export const selectUser = (user: UserInfoResponse | undefined) => user

export const selectUserInfo = (state: RootState) => state.userInfo

export const selectUserFirstName = createSelector(
  selectUser,
  (user?: UserInfoResponse) => user?.firstname,
)

export const selectUserLastName = createSelector(
  selectUser,
  (user?: UserInfoResponse) => user?.lastname,
)

export const selectUserMiddleName = createSelector(
  selectUser,
  (user?: UserInfoResponse) => user?.middleName,
)

export const selectUserFullname = createSelector(
  selectUser,
  (user?: UserInfoResponse) => `${user?.firstname} ${user?.lastname}`,
)

export const selectUserProfilePicUrl = createSelector(
  selectUser,
  (user?: UserInfoResponse) => user?.profilePicUrl,
)

export const selectProfileImage = (state: RootState) =>
  state.userInfo.profileImgUrl

export const selectUserName = (state: RootState) => {
  return `${state.userInfo.firstname} ${state.userInfo.lastname}`
}

export const selectUserEmail = createSelector(
  selectUser,
  (user?: UserInfoResponse) => `${user?.email}`,
)

export const selectUserPhoneNumber = createSelector(
  selectUser,
  (user?: UserInfoResponse) => `${user?.phoneNumber}`,
)

export const selectUserConfirmInformation = createSelector(
  selectUserInfo,
  (userInfo) => ({
    firstname: userInfo.firstname,
    lastname: userInfo.lastname,
    email: userInfo.email,
  }),
)

export const selectAccount = (state: RootState) => state.userInfo.account

////////////////////////////////////////////////////////////////////////
// KYC selectors
export const selectKYCStatus = (state: RootState) =>
  state.userInfo.account.statusKYC

export const selectKYCInquiryId = (state: RootState) =>
  state.userInfo.account.KYCInquiryId

////////////////////////////////////////////////////////////////////////
// Credits & Suscription selectors
export const selectCreditsFromStore = (state: RootState) =>
  state.userInfo.credits

export const selectImpersonationCreditsFromStore = (state: RootState) =>
  state.userInfo.impersonationCredits

export const selectHasActiveSubscription = (state: RootState) =>
  state.userInfo.account.plan?.isActive

export const selectPlan = (state: RootState) => state.userInfo.account.plan

////////////////////////////////////////////////////////////////////////
// Role and permission selectors
export const selectPermissions = (state: RootState) =>
  state.userInfo.permissions

export const selectRole = (state: RootState) => state.userInfo.role

export const selectIsPending = createSelector(
  selectUser,
  (user?: UserInfoResponse) => user?.status === 'pending',
)

export const selectItsDefaultState = (state: RootState) =>
  state.userInfo.email === ''

export const selectUserFirstLetter = createSelector(
  selectUser,
  (user?: UserInfoResponse) => {
    if (user?.firstname?.[0]) {
      return user.firstname[0].toUpperCase()
    } else if (user?.lastname?.[0]) {
      return user.lastname[0].toUpperCase()
    }
    return ''
  },
)
